/**
 * Shared frontend input validation — Phase 5.
 *
 * Frozen API surface. Mirrors the Phase 1 backend contract in
 * `backend/app/core/input_validation.py`. Phase 6 (AI inputs) and Phase 7 (all
 * other inputs) consume this module; Phase 5 itself does not wire any UI.
 *
 * Sign-off (Phase 5):
 *   - frontend-engineer:  see PR
 *   - security-engineer:  DOMPurify config reviewed
 *   - qa-testing-engineer: unit + RTL tests + parity CI
 *
 * Rules:
 *   1. Pure where possible; no network, no `Date.now`, no `console.*`.
 *   2. Errors are advisory — backend is the source of truth.
 *   3. Never `throw` on validation failure — return a `ValidationResult`.
 *   4. `MAX_LENGTHS` values MUST byte-equal the backend `PROFILES` caps.
 *      Drift is enforced by `backend/tests/security/test_frontend_cap_parity.py`.
 */

import { useCallback } from 'react';
import DOMPurify from 'dompurify';

// ---------------------------------------------------------------------------
// Types (JSDoc)
// ---------------------------------------------------------------------------

/**
 * @typedef {(
 *   'email' | 'password' | 'display_name' | 'short_text' | 'long_text' |
 *   'huge_html' | 'identifier' | 'url' | 'csv_cell' | 'ai_claim' |
 *   'ai_marketing_long' | 'ai_marketing_short' | 'ai_marketing_keyword' |
 *   'product_page' | 'analyze_text'
 * )} ProfileName
 */

/**
 * @typedef {Object} ProfileSpec
 * @property {number}  maxLength
 * @property {boolean} allowEmpty
 * @property {boolean} allowNewlines
 * @property {RegExp=} pattern
 * @property {('email'|'trim'|null)=} normalize
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} ok
 * @property {('INPUT_REQUIRED'|'INPUT_TOO_LONG'|'INPUT_INVALID'|'INPUT_HEADER_INJECTION')=} error_code
 * @property {string=} message
 * @property {string=} normalized
 */

// ---------------------------------------------------------------------------
// Profile table — mirrors backend/app/core/input_validation.py PROFILES
// ---------------------------------------------------------------------------

// Regexes (kept identical in spirit to backend, ASCII-safe subsets where the
// backend uses broader Unicode classes; backend remains authoritative).
const DISPLAY_NAME_RE = /^[\p{L}\p{N} \-_.']+$/u;
const IDENTIFIER_RE   = /^[A-Za-z0-9_\-]+$/;
const URL_RE          = /^https?:\/\/[^\s<>"]+$/i;
const EMAIL_RE        = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,63}$/;

/** Numeric caps. SINGLE SOURCE OF TRUTH on the frontend.
 *  Each entry annotated with the backend profile name it mirrors.
 *  @type {Readonly<Record<ProfileName, number>>}
 */
export const MAX_LENGTHS = Object.freeze({
  email:                254,    // mirrors backend: validate_email hard cap
  password:             128,    // mirrors backend: validate_password upper cap
  display_name:         80,     // mirrors backend: PROFILES.display_name
  short_text:           200,    // mirrors backend: PROFILES.short_text
  long_text:            2000,   // mirrors backend: PROFILES.long_text
  huge_html:            100000, // mirrors backend: PROFILES.huge_html
  identifier:           64,     // mirrors backend: PROFILES.identifier
  url:                  2048,   // mirrors backend: PROFILES.url
  csv_cell:             5000,   // mirrors backend: PROFILES.csv_cell
  ai_claim:             20000,  // mirrors backend: PROFILES.ai_claim (raised 2000->20000, Phase 1.1)
  ai_marketing_long:    2000,   // mirrors backend: PROFILES.ai_marketing_long
  ai_marketing_short:   200,    // mirrors backend: PROFILES.ai_marketing_short
  ai_marketing_keyword: 50,     // mirrors backend: PROFILES.ai_marketing_keyword
  product_page:         100000, // mirrors backend: PROFILES.product_page
  analyze_text:         300,    // mirrors backend: PROFILES.analyze_text (one claim, ~45-50 words; was 3000, 8-16)
});

/** Full profile specs.
 *  @type {Readonly<Record<ProfileName, ProfileSpec>>}
 */
export const PROFILES = Object.freeze({
  email: {
    maxLength: MAX_LENGTHS.email,
    allowEmpty: false,
    allowNewlines: false,
    pattern: EMAIL_RE,
    normalize: 'email',
  },
  password: {
    maxLength: MAX_LENGTHS.password,
    allowEmpty: false,
    allowNewlines: false,
    pattern: null,
    normalize: null,
  },
  display_name: {
    maxLength: MAX_LENGTHS.display_name,
    allowEmpty: false,
    allowNewlines: false,
    pattern: DISPLAY_NAME_RE,
    normalize: 'trim',
  },
  short_text: {
    maxLength: MAX_LENGTHS.short_text,
    allowEmpty: true,
    allowNewlines: false,
    pattern: null,
    normalize: 'trim',
  },
  long_text: {
    maxLength: MAX_LENGTHS.long_text,
    allowEmpty: true,
    allowNewlines: true,
    pattern: null,
    normalize: null,
  },
  huge_html: {
    maxLength: MAX_LENGTHS.huge_html,
    allowEmpty: true,
    allowNewlines: true,
    pattern: null,
    normalize: null,
  },
  identifier: {
    maxLength: MAX_LENGTHS.identifier,
    allowEmpty: false,
    allowNewlines: false,
    pattern: IDENTIFIER_RE,
    normalize: null,
  },
  url: {
    maxLength: MAX_LENGTHS.url,
    allowEmpty: true,
    allowNewlines: false,
    pattern: URL_RE,
    normalize: 'trim',
  },
  csv_cell: {
    maxLength: MAX_LENGTHS.csv_cell,
    allowEmpty: true,
    allowNewlines: true,
    pattern: null,
    normalize: 'trim',
  },
  ai_claim: {
    maxLength: MAX_LENGTHS.ai_claim,
    allowEmpty: false,
    allowNewlines: true,
    pattern: null,
    normalize: null,
  },
  ai_marketing_long: {
    maxLength: MAX_LENGTHS.ai_marketing_long,
    allowEmpty: false,
    allowNewlines: true,
    pattern: null,
    normalize: null,
  },
  ai_marketing_short: {
    maxLength: MAX_LENGTHS.ai_marketing_short,
    allowEmpty: false,
    allowNewlines: false,
    pattern: null,
    normalize: 'trim',
  },
  ai_marketing_keyword: {
    maxLength: MAX_LENGTHS.ai_marketing_keyword,
    allowEmpty: false,
    allowNewlines: false,
    pattern: null,
    normalize: 'trim',
  },
  product_page: {
    maxLength: MAX_LENGTHS.product_page,
    allowEmpty: true,
    allowNewlines: true,
    pattern: null,
    normalize: null,
  },
  // Dashboard analyze box (CU-Based-pricing-3000-char-8-13.md). allowEmpty
  // stays true so an empty box reports its own "enter some text" error rather
  // than a validation failure; over-length surfaces as INPUT_TOO_LONG, which
  // App.js treats as a SOFT failure (the Analyze button stays clickable so the
  // click can explain itself) unlike every other error_code here.
  analyze_text: {
    maxLength: MAX_LENGTHS.analyze_text,
    allowEmpty: true,
    allowNewlines: true,
    pattern: null,
    normalize: null,
  },
});

// ---------------------------------------------------------------------------
// DOMPurify config (frozen)
// ---------------------------------------------------------------------------

export const SANITIZE_CONFIG = Object.freeze({
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'ul', 'ol', 'li', 'br', 'span'],
  ALLOWED_ATTR: ['href', 'title', 'lang'],
  ALLOWED_URI_REGEXP: /^https?:\/\//i,
  FORBID_TAGS: ['style', 'script'],
  FORBID_ATTR: ['style'],
  RETURN_TRUSTED_TYPE: false,
});

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

// Control chars: C0 except \t \n, plus DEL and C1.
// eslint-disable-next-line no-control-regex
const CTRL_RE_KEEP_NL = /[\x00-\x08\x0b-\x1f\x7f-\x9f]/g;
// eslint-disable-next-line no-control-regex
const CTRL_RE_STRICT  = /[\x00-\x1f\x7f-\x9f]/g;

// CSV-injection sentinels at start of a cell.
const CSV_BAD_START = /^[=+\-@]/;

function nfcNormalize(s) {
  try { return s.normalize('NFC'); } catch (_e) { return s; }
}

function stripControls(value, allowNewlines) {
  return value.replace(allowNewlines ? CTRL_RE_KEEP_NL : CTRL_RE_STRICT, '');
}

function applyNormalize(value, kind) {
  if (kind === 'email') return value.trim().toLowerCase();
  if (kind === 'trim')  return value.trim();
  return value;
}

function fail(error_code, message) {
  return { ok: false, error_code, message };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Validate a text value against a profile.
 * Pure: no side effects.
 *
 * @param {string} value
 * @param {ProfileName} profileName
 * @returns {ValidationResult}
 */
export function validateText(value, profileName) {
  const profile = PROFILES[profileName];
  if (!profile) {
    return fail('INPUT_INVALID', `Unknown validation profile: ${profileName}`);
  }
  if (typeof value !== 'string') {
    return fail('INPUT_INVALID', 'Value must be a string.');
  }

  // CR/LF / NUL screen happens BEFORE normalisation for fields that disallow
  // newlines — mirrors backend `validate_email` which checks raw \r\n.
  if (!profile.allowNewlines && /[\r\n]/.test(value)) {
    return fail('INPUT_HEADER_INJECTION', 'Field must not contain newline characters.');
  }
  // NUL is never allowed.
  if (value.indexOf('\u0000') !== -1) {
    return fail('INPUT_INVALID', 'Field contains invalid characters.');
  }

  let cleaned = nfcNormalize(value);
  cleaned = stripControls(cleaned, profile.allowNewlines);
  cleaned = applyNormalize(cleaned, profile.normalize);

  // Required check is post-trim so "   " counts as empty for trimmed profiles.
  const isEmpty = cleaned.length === 0;
  if (isEmpty && !profile.allowEmpty) {
    return fail('INPUT_REQUIRED', 'Field is required.');
  }

  if (cleaned.length > profile.maxLength) {
    return fail(
      'INPUT_TOO_LONG',
      `Field exceeds the maximum allowed length of ${profile.maxLength} characters.`,
    );
  }

  // Profile-specific extra rules.
  if (profileName === 'password' && !isEmpty && cleaned.length < 8) {
    return fail('INPUT_INVALID', 'Password must be at least 8 characters.');
  }
  if (profileName === 'csv_cell' && !isEmpty && CSV_BAD_START.test(cleaned)) {
    return fail('INPUT_INVALID', 'Field must not start with =, +, -, or @.');
  }

  if (profile.pattern && !isEmpty && !profile.pattern.test(cleaned)) {
    return fail('INPUT_INVALID', 'Field contains disallowed characters.');
  }

  const result = { ok: true };
  if (profile.normalize) result.normalized = cleaned;
  return result;
}

/**
 * Characters remaining within a profile's cap. Negative when over.
 *
 * @param {string} value
 * @param {ProfileName} profileName
 * @returns {number}
 */
export function getRemaining(value, profileName) {
  const profile = PROFILES[profileName];
  if (!profile) return 0;
  const len = typeof value === 'string' ? value.length : 0;
  return profile.maxLength - len;
}

/**
 * Entity-escape a string for safe display in contexts where React's default
 * escaping is bypassed (CSV export, copy-to-clipboard helpers, attribute
 * fallbacks). NEVER used with `dangerouslySetInnerHTML`.
 *
 * @param {string} value
 * @returns {string}
 */
export function escapeForDisplay(value) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * DOMPurify wrapper — only for genuine rich-text surfaces. Centralised so the
 * allowlist is auditable in one place.
 *
 * @param {string} html
 * @returns {string}
 */
export function sanitizeHtml(html) {
  if (html === null || html === undefined) return '';
  // DOMPurify mutates config; pass a shallow clone of the frozen object.
  return DOMPurify.sanitize(String(html), { ...SANITIZE_CONFIG });
}

/**
 * React hook returning an `onPaste` handler that truncates pasted content to
 * `maxLen` characters and fires `onOverflow(originalLength)` exactly once per
 * paste event when truncation actually happens.
 *
 * The returned handler is referentially stable across renders when its
 * arguments are stable (useCallback).
 *
 * @param {number} maxLen
 * @param {(originalLength: number) => void} [onOverflow]
 * @returns {(event: React.ClipboardEvent) => void}
 */
/**
 * Internal: build a paste handler. Exposed for unit tests without React.
 * @private
 */
export function _makeCappedPasteHandler(maxLen, onOverflow) {
  return function cappedPasteHandler(event) {
    try {
      const pasted = event && event.clipboardData
        ? event.clipboardData.getData('text')
        : '';
      if (typeof pasted !== 'string' || pasted.length <= maxLen) {
        // Let React's default paste handling proceed.
        return;
      }
      event.preventDefault();
      const truncated = pasted.slice(0, maxLen);

      const target = event.target;
      if (target && typeof target.setRangeText === 'function') {
        const start = target.selectionStart != null ? target.selectionStart : (target.value || '').length;
        const end   = target.selectionEnd   != null ? target.selectionEnd   : (target.value || '').length;
        target.setRangeText(truncated, start, end, 'end');
        if (typeof target.dispatchEvent === 'function' && typeof Event === 'function') {
          target.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else if (typeof document !== 'undefined' && document.execCommand) {
        document.execCommand('insertText', false, truncated);
      }

      if (typeof onOverflow === 'function') {
        onOverflow(pasted.length);
      }
    } catch (_e) {
      // Never break a paste due to validation glue.
    }
  };
}

export function useCappedPaste(maxLen, onOverflow) {
  return useCallback(_makeCappedPasteHandler(maxLen, onOverflow), [maxLen, onOverflow]);
}
